import { useIntl } from 'react-intl';
import { HttpError } from '@rossum/api-client/errors';
import { IconPlayerPlay } from '@rossum/ui/icons/tabler';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import { useInvokeHook } from '../../../../extension-interop/hooks/useInvokeHook';

const getInvokeErrorMessage = (error: unknown): string => {
  if (error instanceof HttpError) {
    const errorData = error.data as
      | { detail?: string; code?: string }
      | undefined;
    if (
      Array.isArray(errorData) &&
      errorData.every(item => typeof item === 'string')
    ) {
      return errorData.join(', ');
    }
    if (errorData?.detail) {
      return errorData.detail;
    }
    return `HTTP ${error.code}: ${error.endpoint}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (error === null || error === undefined) {
    return 'Unknown error';
  }
  return JSON.stringify(error);
};

type InvokeButtonProps = {
  noChanges: boolean;
  hookUrl: string;
};

export const InvokeButton = ({ noChanges, hookUrl }: InvokeButtonProps) => {
  const intl = useIntl();
  const {
    mutate: invokeHook,
    status: invokeStatus,
    error: invokeError,
  } = useInvokeHook();

  return (
    <Stack spacing={2}>
      <Tooltip
        title={intl.formatMessage(
          !noChanges
            ? {
                id: 'containers.settings.extensions.createExtension.events.invoke.saveFirst',
              }
            : {
                id: 'containers.settings.extensions.createExtension.events.invoke.tooltip',
              }
        )}
      >
        <Box component="span" sx={{ alignSelf: 'flex-start' }}>
          <Button
            variant="outlined"
            startIcon={
              invokeStatus === 'loading' ? (
                <CircularProgress size={20} />
              ) : (
                <SvgIcon fontSize="large">
                  <IconPlayerPlay />
                </SvgIcon>
              )
            }
            onClick={() => invokeHook({ hook: hookUrl })}
            disabled={invokeStatus === 'loading' || !noChanges}
          >
            {intl.formatMessage({
              id: 'containers.settings.extensions.createExtension.events.invoke.button',
            })}
          </Button>
        </Box>
      </Tooltip>
      {invokeError ? (
        <Alert severity="error">
          {intl.formatMessage(
            {
              id: 'containers.settings.extensions.createExtension.events.invoke.error',
            },
            {
              error: getInvokeErrorMessage(invokeError),
            }
          )}
        </Alert>
      ) : null}
    </Stack>
  );
};
